import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { validate } from 'class-validator';
import {
	AccountingEventType,
	ContractState,
	ContractType,
	Currency,
	currencyList,
	CurrencyListItem,
	NetTerms,
	PricingType,
	PURCHASE_ORDER_DOCUMENT_TYPE,
} from '@received/pricing-model';
import { plainToInstance } from 'class-transformer';
import creatingContract from '../../../assets/lottie/creatingContract.json';
import {
	Badge,
	Button,
	Icon,
	Input,
	Tooltip,
	ActionModal,
	PDFPreviewModal,
	PDFPreview,
	CreateIndicator,
	CloseButton,
	VerticalStepper,
	NetTermsSelect,
	CreateCustomer,
} from '../../../components';
import { Step } from '../../../components/_uiComponents/VerticalStepper/Step/Step';
import { PricingTermsV2 } from '../../../components/PricingTerms/PricingTermsV2';
import { UpdatePricingTerms } from '../../../components/TermsAndSettings/TermsAndSettings';
import { GreatJobModal } from './GreatJobModal/GreatJobModal';
import { ReviewContent } from './ReviewModal/ReviewContent';
import { setSidebarIsOpen } from '../../../storeSlices/generalSlice';
import { httpService } from '../../../services/httpService/httpService';
import { PathsConfig } from '../../../services/httpService/configPaths';
import { Contract as ContractData, ContractValidation, PricingModel, PricingModelTab } from '../../../types/contractTypes';
import {
	contractTabs,
	createContractDocuments,
	deleteContract,
	formatValidationError,
	getContractError,
	getContractsDocuments,
	getCustomerSelectedById,
} from './Contract.utils';
import { successErrorMassageOptions } from '../../../components/SuccessErrorModal/SuccessErrorModal.utils';
import { useDebounce } from '../../../hooks/generalHooks';
import { setOpenSuccessErrorModal } from '../../../storeSlices/errorSuccessSlice';
import { images } from '../../../constants/images';
import { Customer } from '../../../types/customerTypes';
import { Sequence } from '../../../types/dunningTypes';
import { DatePicker, DatePickerFormatType, Select } from '../../../components/_uiComponents';
import { FooterTypeOptions } from '../../../components/_uiComponents/DatePicker/Footer/Footer';
import { getBillingStartDate } from '../../../components/PricingModelTabContent/PricingModelTabContentActions';
import { contractStateColor } from '../../../components/_customerTabs/ContractsTab/ContractsTab.utils';
import { BillingDocumentType } from '../../../types/generalTypes';
import { startGenerateInvoicePDFById } from '../../../utils/InvoiceUtils';
import { startGenerateCreditNotePDFById } from '../../../utils/CreditNoteUtils';
import { isEmbedded } from '../../../services/embedded/embeddedService';
import { notifyContractPublished, ContractPublishedAction } from '../../../utils/notifyContractPublished';
import styles from './Contract.module.scss';

interface ContractProps {
	contractType: ContractType;
	mainUrlToReturn: string;
}

// Up-to-two-letter avatar initials from a customer name ("Northwind Trading" → "NW", "acme" → "AC").
const getCustomerInitials = (name?: string) => {
	const parts = (name || '').trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return '—';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const ContractV2 = ({ contractType, mainUrlToReturn }: ContractProps) => {
	const [showGreatJobModal, setShowGreatJobModal] = useState(false);
	const [customerSelected, setCustomerSelected] = useState<Customer>();
	const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
	const [contractData, setContractData] = useState<ContractData>({
		pricingModel: { tabs: [], sourcePricingModelIds: [] },
	});
	const [contractDataFromServer, setContractDataFromServer] = useState<ContractData>({
		pricingModel: { tabs: [] },
	});

	const [validationErrors, setValidationErrors] = useState<string[]>([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const [showExitViewModeModal, setShowExitViewModeModal] = useState(false);
	const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
	const [documentsList, setDocumentsList] = useState<Array<BillingDocumentType>>([]);
	// First-invoice preview drawer (right side): generated on open, refreshable manually.
	const [isFirstInvoiceOpen, setIsFirstInvoiceOpen] = useState(false);
	const [firstInvoiceDoc, setFirstInvoiceDoc] = useState<BillingDocumentType>();
	const [firstInvoiceLoading, setFirstInvoiceLoading] = useState(false);
	const [activeTab, setActiveTab] = useState(0);
	const [showCreatingLoader, setShowCreatingLoader] = useState(false);
	const [productsSelectedTab, setProductsSelectedTab] = useState(0);
	// Bumped to force the load effect to re-fetch the contract.
	const [reloadTrigger, setReloadTrigger] = useState(0);
	// Wizard step index; only the active step's content renders.
	const [activeStep, setActiveStep] = useState(0);
	// Payment Terms live per line item (collective tabs). Before the first item exists there's no tab
	// to hold the value, so keep the Terms-step choice here until one is added, then it's inherited.
	const [contractNetTermsOverride, setContractNetTermsOverride] = useState<NetTerms | undefined>(undefined);

	const { t } = useTranslation('translation');
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location: any = useLocation();
	const { id: contractIdParam } = useParams();
	const updateContractTimer = useRef<NodeJS.Timeout>(null);
	const updateContractSeq = useRef(0);
	// Synchronous double-submit guard: state updates are async, so a rapid double-click on Update would fire
	// the edit/regenerate twice before the button re-renders disabled.
	const isPublishingRef = useRef(false);

	// Embedded (external page) mode hides internal nav; sourced from the app-wide flag so it
	// persists past the initial `?embeddedPage=true` URL.
	const embeddedPage = isEmbedded();

	const isEditActiveContract = !!contractData?.editBlockers?.length && isEditMode;
	const isPartner = [ContractType.REFERRAL, ContractType.RESELLER].includes(contractType);
	const singleCustomerUrl = isPartner
		? `../partners/${contractType === ContractType.RESELLER ? 'single-reseller' : 'single-referral'}/`
		: '../customers/single-customer/';

	useEffect(() => {
		dispatch(setSidebarIsOpen(false));
		location?.state?.customer && setCustomerSelected(location.state.customer);
		const contractId = location?.state?.contractId || contractIdParam;
		contractId && getContractData(contractId);
	}, [reloadTrigger]);

	useEffect(() => {
		validateContract();
	}, [customerSelected, contractData.pricingModel]);

	useEffect(() => {
		!!contractData?.pricingModel?.tabs?.length && setInitialSequenceId();
	}, [contractData?.pricingModel?.tabs?.length]);

	let isMultipleCurrency = false;
	if (contractData?.pricingModel?.tabs?.length) {
		const currency = contractData.pricingModel?.tabs[0]?.currency;

		contractData?.pricingModel?.tabs.forEach((tab) => {
			if (tab.currency != currency) {
				isMultipleCurrency = true;
			}
		});
	}

	const setInitialSequenceId = async () => {
		try {
			const res = (
				await httpService({
					dispatch,
					path: PathsConfig.getSequences,
				})
			).data;
			setContractData((prev) => ({
				...prev,
				pricingModel: {
					...prev.pricingModel,
					dunningSequenceId: prev.pricingModel?.dunningSequenceId ?? res.find((seq: Sequence) => seq.isDefault)?.id,
					isDunningActive: prev.pricingModel?.isDunningActive ?? !!res.find((seq: Sequence) => seq.isDefault),
				},
			}));
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	const getContractData = async (contractId: string) => {
		try {
			const newContract = (
				await httpService({
					dispatch,
					path: PathsConfig.getContractData,
					urlParam: { id: contractId },
				})
			).data;
			let customer: Customer;
			if (!customerSelected) {
				customer = await getCustomerSelectedById(newContract.customerId, dispatch);
				setCustomerSelected(customer);
			}

			// copy contract state to tab so it can be checked down the component tree
			newContract.pricingModel?.tabs?.forEach((tab: PricingModelTab) => {
				tab.contractState = newContract.state;
			});
			setIsEditMode(newContract.state === ContractState.DRAFT);
			// Empty contract has null server activation dates; preserve client-set Contract Period across
			// the refetch so it isn't wiped by the raw server DTO.
			const withPreservedDates = (prev?: ContractData) => ({
				...newContract,
				activationStartDate: newContract.activationStartDate ?? prev?.activationStartDate,
				activationEndDate: newContract.activationEndDate ?? prev?.activationEndDate,
			});
			setContractData(withPreservedDates);
			setContractDataFromServer(withPreservedDates);
			setProductsSelectedTab(0);
			if (newContract.state !== ContractState.ACTIVE) {
				// Products is the only tab in V2.
				setActiveTab(0);
			}
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	const updateContract = async (newContractData: ContractData): Promise<boolean> => {
		try {
			if (!contractData?.editBlockers?.length) {
				const transformedContractData = plainToInstance(ContractData, newContractData);
				const seq = ++updateContractSeq.current;
				const updatedContract: ContractData = (
					await httpService({
						dispatch,
						showLoader: false,
						path: PathsConfig.updateContract,
						data: transformedContractData,
					})
				).data;
				if (seq !== updateContractSeq.current) return false;
				// Empty contract returns null server activation dates; preserve client-set Contract Period
				// so it sticks (and is inherited by tabs added later).
				const preservedStartDate = updatedContract?.activationStartDate ?? newContractData?.activationStartDate;
				const preservedEndDate = updatedContract?.activationEndDate ?? newContractData?.activationEndDate;
				setContractDataFromServer({ ...updatedContract, activationStartDate: preservedStartDate, activationEndDate: preservedEndDate });

				setContractData((prev) => {
					// reuse server-generated ids for each pricingModel
					prev.activationEndDate = preservedEndDate;
					prev.activationStartDate = preservedStartDate;

					// guard prev.pricingModel — crashes on empty state
					if (updatedContract?.pricingModel && prev.pricingModel) {
						prev.pricingModel.id = updatedContract?.pricingModel?.id;
						updatedContract?.pricingModel?.tabs.forEach((item, index) => {
							if (index < prev.pricingModel.tabs.length) {
								// Don't destructure item/tab here — causes a loop and page refresh
								prev.pricingModel.tabs[index].id = item.id;
								prev.pricingModel.tabs[index].index = item.index;
								prev.pricingModel.tabs[index].pricingModelTable = item.pricingModelTable;
							}
						});
					}
					return { ...prev };
				});
			}
			return true;
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
			return false;
		}
	};

	const validateContract = async () => {
		const instance = plainToInstance(ContractValidation, { customer: customerSelected, contract: contractData });
		const errors = await validate(instance);
		setValidationErrors(formatValidationError(errors, t, isMultipleCurrency));
	};

	const setContractPricingModel = (newPricingModel: PricingModel, instantUpdate?: boolean) => {
		// reuse pricing model id from prev, don't create in container
		setContractData((prev) => {
			// New line items inherit contract-wide Payment Terms/Invoice date; existing (by id) and
			// Separate-Invoice (isCollective === false) items keep their own values.
			let incomingPricingModel = newPricingModel;
			if (newPricingModel?.tabs) {
				const prevTabs = prev.pricingModel?.tabs || [];
				const sharedTab = prevTabs.find((tab) => tab.isCollective !== false);
				// Inherit from the existing shared tab, or fall back to the Terms-step choice
				// (contractNetTermsOverride) for the first line item.
				if (sharedTab || contractNetTermsOverride !== undefined) {
					const prevTabIds = new Set(prevTabs.map((tab) => tab.id).filter(Boolean));
					incomingPricingModel = {
						...newPricingModel,
						tabs: newPricingModel.tabs.map((tab) => {
							const isNewTab = !tab.id || !prevTabIds.has(tab.id);
							if (!(isNewTab && tab.isCollective !== false)) {
								return tab;
							}
							return {
								...tab,
								netTerms: sharedTab ? sharedTab.netTerms : (contractNetTermsOverride as NetTerms),
								...(sharedTab ? { issueDay: sharedTab.issueDay } : {}),
							};
						}),
					};
				}
			}
			const newContract = { ...prev, ...{ pricingModel: { ...prev.pricingModel, ...incomingPricingModel } } };
			if (instantUpdate) {
				updateContractTimer.current && clearTimeout(updateContractTimer.current);
				updateContract(newContract);
			} else {
				onUpdateServer(newContract);
			}
			return newContract;
		});
	};

	const setContractMetadata = (field: string, value?: any) => {
		setContractData((prev) => {
			const newContract = { ...prev, [field]: value };
			onUpdateServer(newContract);
			return newContract;
		});
	};

	// Contract-level Billing Period pushed to every tab; each tab's Issue Day recomputed from its
	// own billing cycle against the new period.
	const onUpdateContractPeriod = (fromDate?: Date | null, toDate?: Date | null) => {
		setContractData((prev) => {
			const tabs = (prev.pricingModel?.tabs || []).map((tab) => ({
				...tab,
				billingStartDate: fromDate ?? null,
				billingEndDate: toDate ?? null,
				issueDay: tab.billingCycle ? getBillingStartDate(tab.billingCycle, fromDate, toDate) : tab.issueDay,
			}));
			const newContract = {
				...prev,
				activationStartDate: fromDate ?? undefined,
				activationEndDate: toDate ?? undefined,
				pricingModel: { ...prev.pricingModel, tabs },
			};
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			updateContract(newContract);
			return newContract;
		});
	};

	// +N year shortcut: keep start (today if unset), set end = start + N years − 1 day, so a 1-year
	// term runs Jul 26 2026 → Jul 25 2027 (mirrors DatePicker's YearsFooter).
	const onApplyYearShortcut = (numberOfYears: number) => {
		const fromDate = contractData?.activationStartDate ?? new Date();
		const toDate = dayjs(fromDate).add(numberOfYears, 'year').subtract(1, 'day').toDate();
		onUpdateContractPeriod(fromDate, toDate);
	};

	// Highlight the chip whose duration matches the current period so the selection reads back.
	const activeYearShortcut = useMemo(() => {
		const from = contractData?.activationStartDate;
		const to = contractData?.activationEndDate;
		if (!from || !to) return null;
		return [1, 2, 3].find((num) => dayjs(from).add(num, 'year').subtract(1, 'day').isSame(dayjs(to), 'day')) ?? null;
	}, [contractData?.activationStartDate, contractData?.activationEndDate]);

	// Contract-level currency applied to every tab and each row's itemPrice.
	const onUpdateContractCurrency = (currency: Currency) => {
		setContractData((prev) => {
			const tabs = (prev.pricingModel?.tabs || []).map((tab) => ({
				...tab,
				currency,
				pricingModelTable: {
					...tab.pricingModelTable,
					rows: (tab.pricingModelTable?.rows || []).map((row) => (row?.itemPrice ? { ...row, itemPrice: { ...row.itemPrice, currency } } : row)),
				},
			}));
			const newContract = { ...prev, pricingModel: { ...prev.pricingModel, tabs } };
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			updateContract(newContract);
			return newContract;
		});
	};

	// Contract-wide Payment Terms pushed to collective line items; Separate-Invoice
	// (isCollective === false) items are billed on their own invoice and keep their own values.
	const onUpdateContractPaymentTerms = (netTerms: NetTerms) => {
		// Remember choice so line items added later inherit it, even when none exist yet.
		setContractNetTermsOverride(netTerms);
		setContractData((prev) => {
			const prevTabs = prev.pricingModel?.tabs || [];
			// All-separate-invoice: no collective tab, so persist onto the tabs[0] fallback too — else the
			// choice lives only in transient contractNetTermsOverride and is lost on reload (docs keep stale netTerms).
			const hasCollective = prevTabs.some((tab) => tab.isCollective !== false);
			const tabs = prevTabs.map((tab, index) =>
				tab.isCollective !== false || (!hasCollective && index === 0) ? { ...tab, netTerms } : tab,
			);
			const newContract = { ...prev, pricingModel: { ...prev.pricingModel, tabs } };
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			updateContract(newContract);
			return newContract;
		});
	};

	// Backs the Terms-step Payment Terms control: first collective item (falls back to tabs[0]).
	const sharedTermsTab = contractData?.pricingModel?.tabs?.find((tab) => tab.isCollective !== false) ?? contractData?.pricingModel?.tabs?.[0];

	// Contract-level settings (Debit Entity / Payment Method / Bank Transfer / Note) applied to every tab.
	const onUpdateContractTerms = ({ note, supplierId, billingDetails, cryptoSettings }: UpdatePricingTerms, instantUpdate?: boolean) => {
		setContractData((prev) => {
			// Crypto settings are contract-level values but a one-to-one row per tab, so each tab must keep its
			// own id — sharing one fails the unique constraint on save.
			const { id: _sharedCryptoSettingsId, ...cryptoSettingsValues } = cryptoSettings ?? {};
			const tabs = (prev.pricingModel?.tabs || []).map((tab) => ({
				...tab,
				supplierId,
				note,
				billingDetails: { ...tab.billingDetails, ...billingDetails },
				cryptoSettings: { ...tab.cryptoSettings, ...cryptoSettingsValues },
			}));
			const newContract = { ...prev, pricingModel: { ...prev.pricingModel, tabs } };
			if (instantUpdate) {
				updateContractTimer.current && clearTimeout(updateContractTimer.current);
				updateContract(newContract);
			} else {
				onUpdateServer(newContract);
			}
			return newContract;
		});
	};

	const onDoneEditingContract = async (contractData: ContractData) => {
		try {
			setShowCreatingLoader(true);
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			const transformedContractData = plainToInstance(ContractData, contractData);
			await httpService({
				dispatch,
				showLoader: false,
				path: PathsConfig.publishContract,
				data: transformedContractData,
			});

			setShowCreatingLoader(false);
			onPublishSuccess('created');
		} catch (error) {
			setShowCreatingLoader(false);
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	// In embedded mode skip the "Great Job" page and reload the published contract in place.
	const onPublishSuccess = (action: ContractPublishedAction) => {
		notifyContractPublished(contractData.id, action);
		if (embeddedPage) {
			setActiveTab(0);
			setReloadTrigger((value) => value + 1);
		} else {
			setShowGreatJobModal(true);
		}
	};

	// Loads contract documents: reads persisted when not editing, else saves draft and regenerates.
	// firstInvoiceOnly caps the window to the first billing cycle.
	const loadContractDocuments = async (firstInvoiceOnly?: boolean) => {
		if (!contractData?.id) return undefined;
		updateContractTimer.current && clearTimeout(updateContractTimer.current);
		// View mode, and editing an already-active contract (edit blockers present, e.g. sent invoices),
		// both read persisted documents. An active contract can't persist edits without publishing, and
		// regenerate mutates the DB (drops/recreates non-locked drafts), so it must stay read-only here —
		// this surfaces the real sent + upcoming invoices without side effects. Only a plain draft edit
		// saves the draft first and regenerates so the timeline reflects the in-progress edits.
		if (!isEditMode || contractData?.editBlockers?.length) {
			return getContractsDocuments(dispatch, contractData, firstInvoiceOnly ? 1 : undefined);
		}
		// Bail if the save failed — regenerating would rebuild documents against stale server state.
		const saved = await updateContract(contractData);
		if (!saved) return undefined;
		return createContractDocuments(dispatch, contractData.id, firstInvoiceOnly);
	};

	const onOpenPDFPreview = async () => {
		try {
			const documentData = await loadContractDocuments();
			documentData && setDocumentsList(documentData);
			setIsPDFViewerOpen(true);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	// Download the currently-previewed timeline document as a PDF (invoice or credit note).
	const onDownloadTimelineDocument = (document: BillingDocumentType) => {
		// Purchase orders are filtered out of the timeline and can't be downloaded here.
		if (!document || document.accountingEventType === PURCHASE_ORDER_DOCUMENT_TYPE) return;

		if (document.accountingEventType === AccountingEventType.CREDIT) {
			startGenerateCreditNotePDFById(dispatch, document.id);
		} else {
			startGenerateInvoicePDFById(dispatch, document.id);
		}
	};

	// Generate only the first invoice (server caps to first cycle) for the side drawer; separate from
	// the full Timeline series.
	const generateFirstInvoicePreview = async () => {
		if (!contractData?.id) return;
		try {
			setFirstInvoiceLoading(true);
			const documentData = await loadContractDocuments(true);
			setFirstInvoiceDoc(documentData?.[0]);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		} finally {
			setFirstInvoiceLoading(false);
		}
	};

	const onToggleFirstInvoicePreview = () => {
		const willOpen = !isFirstInvoiceOpen;
		setIsFirstInvoiceOpen(willOpen);
		// Generate once on open; refresh button regenerates afterwards.
		if (willOpen && !firstInvoiceDoc) {
			generateFirstInvoicePreview();
		}
	};

	const errorList =
		validationErrors.length > 1
			? validationErrors.map((error, index) => <li key={index}>{getContractError(error)}</li>)
			: getContractError(validationErrors[0]);

	const onSaveAndClose = async () => {
		try {
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			await updateContract(contractData);
			navigate(`${singleCustomerUrl}${customerSelected?.id}`, { state: { contractId: contractData.id } });
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.FAILED_LOAD_CONTRACT_DATA }));
		}
	};

	const onClose = async () => {
		if (!isEditMode) {
			navigate(`${singleCustomerUrl}${customerSelected?.id}`, { state: { contractId: contractData.id } });
		} else if (customerSelected && contractData.pricingModel?.tabs?.length > 0) {
			onSaveAndClose();
		} else {
			onDelete();
		}
	};

	const onDelete = async () => {
		updateContractTimer.current && clearTimeout(updateContractTimer.current);
		contractData.id
			? await deleteContract(contractData.id, dispatch, handleDeleteContract)
			: navigate(customerSelected?.id ? `${singleCustomerUrl}${customerSelected?.id}` : mainUrlToReturn);
	};

	// Review & Publish step: persist draft and refetch so the review shows server-computed totals.
	const prepareReview = async () => {
		// todo - use API to get Contract Summary instead of calling these APIs
		updateContractTimer.current && clearTimeout(updateContractTimer.current);
		await updateContract(contractData);
		await createContractDocuments(dispatch, contractData.id);
		contractData.id && (await getContractData(contractData.id));
	};

	const onPublishActiveContract = async () => {
		if (isPublishingRef.current) return;
		isPublishingRef.current = true;
		try {
			setShowCreatingLoader(true);
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			const transformedContractData = plainToInstance(ContractData, contractData);
			transformedContractData.includeCurrentInvoice = true;
			await httpService({
				dispatch,
				showLoader: false,
				path: PathsConfig.editActiveContract,
				data: transformedContractData,
			});

			setShowCreatingLoader(false);
			onPublishSuccess('updated');
		} catch (error) {
			setShowCreatingLoader(false);
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		} finally {
			isPublishingRef.current = false;
		}
	};

	const handleDeleteContract = async () => {
		setContractData((prev) => ({ ...prev, id: undefined }));
		customerSelected?.id ? navigate(`${singleCustomerUrl}${customerSelected?.id}`) : navigate(mainUrlToReturn);
	};

	const onUpdateCustomer = async (customer: Customer) => {
		setCustomerSelected(customer);
		const newContract = { ...contractData, customerId: customer ? customer.id : null, customer: customer || null };
		onUpdateServer(newContract);
		setContractData(newContract);
	};

	// After editing the customer in the modal, refetch it so the contract's customer snapshot (name, email,
	// billing address) reflects the change.
	const reloadCustomerDetails = async () => {
		if (!customerSelected?.id) return;
		const refreshed = await getCustomerSelectedById(customerSelected.id, dispatch);
		if (refreshed) {
			setCustomerSelected(refreshed);
			setContractData((prev) => ({ ...prev, customer: refreshed }));
		}
	};

	const onExitViewMode = async () => {
		setShowExitViewModeModal(false);
		setIsEditMode(true);

		if (!contractData?.editBlockers?.length) {
			updateContractTimer.current && clearTimeout(updateContractTimer.current);
			await updateContract(contractData);
			contractData.id && (await getContractData(contractData.id));
			// getContractData resets edit mode to DRAFT-only; re-enter edit mode so non-draft
			// contracts without editBlockers (e.g. Canceled) don't snap back to view mode.
			setIsEditMode(true);
		}
	};

	const isPreviewEnabled = () => !isEditMode || !validationErrors.length;

	const onUpdateServer = useDebounce(isEditMode ? updateContract : () => {}, 5000, updateContractTimer);

	// Top-right action: none while drafting (publishes from footer), "Update" when editing active, "Edit" in view mode.
	const renderPrimaryActionButton = () =>
		contractData?.state == ContractState.DRAFT ? null : isEditActiveContract ? (
			<Button color='success' disabled={!!validationErrors.length || showCreatingLoader} onClick={() => onPublishActiveContract()}>
				{t('update')}
			</Button>
		) : (
			<Button color='success' type='primary' onClick={() => setShowExitViewModeModal(true)}>
				{t('edit')}
				<Icon imgType='edit' color='white' />
			</Button>
		);

	// Single source of truth for the wizard steps: the left-rail stepper, the content header, and the
	// billing-step gate all derive from this, so adding/reordering a step can't silently desync them.
	const stepDefs = useMemo(
		() => [
			{ key: 'terms', title: t('terms'), railSubtitle: t('termsSubtitle'), contentSubtitle: t('termsSubtitle') },
			{ key: 'items', title: t('contractItems'), railSubtitle: t('contractItemsSubtitle'), contentSubtitle: t('contractItemsSubtitle') },
			{ key: 'billing', title: t('billingSettings'), railSubtitle: t('billingStepRailSubtitle'), contentSubtitle: t('billingStepContentSubtitle') },
			{ key: 'review', title: t('reviewAndPublish'), railSubtitle: t('reviewSubtitle'), contentSubtitle: t('reviewSubtitle') },
		],
		[t],
	);
	const billingStepIndex = stepDefs.findIndex((step) => step.key === 'billing');

	// Left-rail steps; state derived from activeStep (earlier DONE, active IN_PROGRESS, later UNSET).
	const steps: Step[] = useMemo(
		() =>
			stepDefs.map((definition, index) => ({
				title: definition.title,
				subTitle: definition.railSubtitle,
				state: (index < activeStep ? 'DONE' : index === activeStep ? 'IN_PROGRESS' : 'UNSET') as Step['state'],
				component: () => <></>,
			})),
		[stepDefs, activeStep],
	);

	const goToStep = (index: number) => setActiveStep(Math.min(steps.length - 1, Math.max(0, index)));

	// A legal entity must be selected on the Billing settings step before continuing — but only once line
	// items exist (with none, tabs is empty and there's nothing to attach a supplier to, so don't dead-end
	// Next). Checks every tab so a later tab missing a supplier is caught too.
	const contractTabsForGate = contractData?.pricingModel?.tabs;
	const legalEntityMissing =
		activeStep === billingStepIndex && !!contractTabsForGate?.length && contractTabsForGate.some((tab) => !tab.supplierId);

	// On landing the Review step (draft only): persist + refetch for latest totals.
	const isReviewStep = activeStep === steps.length - 1;
	useEffect(() => {
		if (isReviewStep && contractData?.state === ContractState.DRAFT) {
			prepareReview();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReviewStep]);

	return (
		<div className={styles.globalPageContainer}>
			<CreateIndicator isShown={showCreatingLoader} lottie={creatingContract} />

			<div className={styles.page}>
				{!embeddedPage && (
					<div className={styles.closeRow}>
						<CloseButton onClick={onClose} />
					</div>
				)}

				<div className={styles.body}>
					{/* Persistent header across every step. */}
					<div className={styles.pageHeader}>
						<div className={styles.titleWithStatus}>
							<h1 className={styles.title}>Billing contract</h1>
							<Badge className={styles.statusTag} color={contractStateColor(contractData).color} type='secondary' dataTestId='contract-badge'>
								{contractStateColor(contractData).text}
							</Badge>
						</div>

						<div className={styles.actionButtons}>
							<Tooltip tooltipComponent={<ul>{errorList}</ul>} disabled={!validationErrors.length} placement='left'>
								<Button
									type='outlined'
									color='neutral'
									onClick={onOpenPDFPreview}
									className={clsx(isPreviewEnabled() && styles.editable)}
									disabled={!isPreviewEnabled()}
								>
									{t('timeline')}
								</Button>
							</Tooltip>

							{isEditActiveContract && (
								<Button
									type='outlined'
									color='neutral'
									onClick={async () => {
										contractData?.id && (await getContractData(contractData?.id));
										setIsEditMode(true);
									}}
								>
									{t('discardChanges')}
								</Button>
							)}

							{renderPrimaryActionButton()}
						</div>
					</div>

					{/* Two-column wizard: left stepper rail + the active step's content. */}
					<div className={styles.wizard}>
						<div className={styles.stepperRail}>
							<div className={styles.field}>
								<div className={styles.fieldLabel}>{t('customer')}</div>
								{(() => {
									const canEditCustomer = isEditMode && !!customerSelected;
									return (
										<div
											className={clsx(styles.customerCard, canEditCustomer && styles.customerCardClickable)}
											role={canEditCustomer ? 'button' : undefined}
											tabIndex={canEditCustomer ? 0 : undefined}
											aria-label={canEditCustomer ? t('edit') : undefined}
											onClick={() => canEditCustomer && setIsEditCustomerOpen(true)}
											onKeyDown={(event) => {
												if (canEditCustomer && (event.key === 'Enter' || event.key === ' ')) {
													event.preventDefault();
													setIsEditCustomerOpen(true);
												}
											}}
										>
											<div className={styles.customerAvatar}>{getCustomerInitials(customerSelected?.name)}</div>
											<div className={styles.customerCardInfo}>
												<span className={styles.customerCardName}>{customerSelected?.name || '—'}</span>
												{customerSelected?.email && (
													<span className={styles.customerCardMeta}>{customerSelected.email}</span>
												)}
											</div>
											{canEditCustomer && (
												<span className={styles.customerCardEdit} aria-hidden>
													<Icon imgType='edit' width={1.4} color='neutral400' />
												</span>
											)}
										</div>
									);
								})()}
							</div>

							<div className={styles.field}>
								<div className={styles.fieldLabel}>{t('PurchaseOrder')}</div>
								<Input
									disabled={!isEditMode}
									onFocus={(e) => e.target.select()}
									placeholder='PO-0000000'
									value={contractData?.poNumber || ''}
									onChange={(val, e) => setContractMetadata('poNumber', e.target.value)}
									onKeyDown={(event: any) => {
										if (event.key === 'Enter') {
											event.currentTarget.blur();
											setContractMetadata('poNumber', event?.target?.value);
										}
									}}
									onBlur={(event: any) => setContractMetadata('poNumber', event?.target?.value)}
									className={clsx(styles.poInput, !isEditMode && styles.notAllowedCursor)}
									type='text'
									rightChildren={<Icon imgType='edit' width={1.2} color='neutral400' />}
								/>
							</div>

							<div className={styles.railDivider} />

							<VerticalStepper numbered stacked stepsList={steps} updateSteps={() => {}} updateActiveIndex={goToStep} />
						</div>

						<div className={styles.stepContent}>
							{/* Consistent header for every step: title + subtitle. */}
							<div className={styles.stepHeader}>
								<span className={styles.stepHeaderTitle}>{stepDefs[activeStep]?.title}</span>
								{stepDefs[activeStep]?.contentSubtitle && (
									<span className={styles.stepHeaderSubtitle}>{stepDefs[activeStep].contentSubtitle}</span>
								)}
							</div>

							{/* Step 1 — Terms: period, currency, payment terms. */}
							{activeStep === 0 && (
								<div className={styles.stepPanel}>
									<div className={styles.termsRow}>
										<div className={styles.field}>
											<div className={styles.fieldLabel}>Billing period</div>
											<DatePicker
												key={`${contractData?.activationStartDate ?? ''}-${contractData?.activationEndDate ?? ''}`}
												rangePicker
												fullBorder
												disabled={!isEditMode}
												showError={isEditMode && !contractData?.activationEndDate}
												footerType={FooterTypeOptions.ADD_YEARS}
												datePickerLabel={t('billingEndDate')}
												defaultFromDate={contractData?.activationStartDate ?? new Date()}
												defaultToDate={contractData?.activationEndDate}
												updateOnClose={(date: DatePickerFormatType) => onUpdateContractPeriod(date.fromDate, date.toDate)}
												className={styles.contractPeriodBox}
												inputClassName={styles.contractPeriodInput}
											/>
											<div className={styles.periodShortcuts}>
												{[1, 2, 3].map((num) => (
													<Button
														key={num}
														type='outlined'
														color='neutral'
														disabled={!isEditMode}
														onClick={() => onApplyYearShortcut(num)}
														className={clsx(styles.periodShortcut, activeYearShortcut === num && styles.periodShortcutActive)}
													>
														+{num} {num > 1 ? t('years') : t('year')}
													</Button>
												))}
											</div>
										</div>

										<div className={styles.field}>
											<div className={styles.fieldLabel}>Currency</div>
											<Select
												key={contractData?.pricingModel?.tabs?.[0]?.currency ?? Currency.USD}
												disabled={!isEditMode}
												onChange={(value: Currency) => onUpdateContractCurrency(value)}
												defaultValue={contractData?.pricingModel?.tabs?.[0]?.currency ?? Currency.USD}
												data={currencyList.map((item: CurrencyListItem) => ({ label: `${item.flag} ${item.currency}`, value: item.currency }))}
												style={{ width: '100%' }}
												className={styles.contractCurrency}
											/>
										</div>

										{/* Contract-wide Payment Terms default; Invoice date is per line item (Contract Items step). */}
										<div className={styles.field}>
											<div className={styles.fieldLabel}>Payment terms</div>
											<NetTermsSelect
												key={`net-${sharedTermsTab?.netTerms ?? contractNetTermsOverride ?? ''}`}
												fullBorder
												style={{ width: '100%' }}
												disabled={!isEditMode}
												defaultValue={sharedTermsTab?.netTerms ?? contractNetTermsOverride}
												isUsageProduct={sharedTermsTab?.pricingType == PricingType.USAGE}
												onChange={(netTerms: any) => onUpdateContractPaymentTerms(netTerms)}
											/>
										</div>
									</div>
								</div>
							)}

							{/* Step 2 — Contract line items. */}
							{activeStep === 1 && (
								<div className={styles.stepPanel}>
									<div className={styles.productsSection}>
										<div className={styles.bottomContainer}>
											{contractTabs(contractType)[activeTab].component({
												selectedCustomer: customerSelected,
												pricingModel: contractData?.pricingModel,
												setPricingModel: setContractPricingModel,
												isEditMode,
												contractState: contractData?.state,
												contract: contractDataFromServer,
												isValid: validationErrors.length === 0,
												defaultSelectedTab: productsSelectedTab,
												contractType,
												isPartner,
												setProductsSelectedTab,
												updateSettings: setContractPricingModel,
												embeddedPage,
											})}
										</div>
									</div>
								</div>
							)}

								{/* Step 3 — Billing settings. Always shown so the Legal Entity can be selected or created via the
								    dropdown regardless of line items; terms bind once a line item exists. */}
							{activeStep === 2 && (
								<div className={styles.stepPanel}>
									<section className={styles.settingsSection}>
										<PricingTermsV2
											hideTitle
											className={styles.contractSettingsInner}
											disabled={!isEditMode}
											// Only a draft may have its billing pre-selection auto-persisted; a non-draft (e.g. a
											// Canceled contract re-entered into edit mode) must not be rewritten by opening this step.
											allowAutoPersist={contractData?.state === ContractState.DRAFT}
											pricingModelTab={contractData?.pricingModel?.tabs?.[0]}
											customerId={customerSelected?.id}
											updatePricingTermsData={onUpdateContractTerms}
										/>
									</section>
								</div>
							)}

							{/* Step 4 — Review & Publish. */}
							{activeStep === 3 && (
								<div className={styles.stepPanel}>
									<ReviewContent customer={customerSelected} contractData={contractDataFromServer} />
								</div>
							)}

							{/* Wizard nav: Back, then Next / Publish. */}
							<div className={styles.stepNav}>
								<div>
									{activeStep > 0 && (
										<Button type='outlined' color='neutral' onClick={() => goToStep(activeStep - 1)}>
											Back
										</Button>
									)}
								</div>
								<div>
									{activeStep < steps.length - 1 ? (
										<Tooltip
											tooltipComponent={<span>{t('selectLegalEntityToContinue')}</span>}
											disabled={!legalEntityMissing}
											placement='top'
										>
											<Button color='success' disabled={legalEntityMissing} onClick={() => goToStep(activeStep + 1)}>
												{t('next')}
											</Button>
										</Tooltip>
									) : contractData?.state === ContractState.DRAFT ? (
										<Tooltip tooltipComponent={<ul>{errorList}</ul>} disabled={!validationErrors.length} placement='top'>
											<Button color='success' disabled={!!validationErrors.length} onClick={() => onDoneEditingContract(contractData)}>
												{t('publishContract')}
											</Button>
										</Tooltip>
									) : isEditActiveContract ? (
										<Tooltip tooltipComponent={<ul>{errorList}</ul>} disabled={!validationErrors.length} placement='top'>
											<Button color='success' disabled={!!validationErrors.length || showCreatingLoader} onClick={() => onPublishActiveContract()}>
												{t('update')}
											</Button>
										</Tooltip>
									) : null}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* First-invoice preview drawer (right side) */}
			<div className={clsx(styles.firstInvoiceDrawer, isFirstInvoiceOpen && styles.firstInvoiceDrawerOpen)}>
				<div className={styles.firstInvoiceDrawerHeader}>
					<span className={styles.firstInvoiceDrawerTitle}>First invoice preview</span>
					<div className={styles.firstInvoiceDrawerActions}>
						<Button type='link' color='neutral' onClick={generateFirstInvoicePreview} disabled={firstInvoiceLoading}>
							Refresh
						</Button>
						<CloseButton onClick={() => setIsFirstInvoiceOpen(false)} />
					</div>
				</div>
				<div className={styles.firstInvoiceDrawerBody}>
					{firstInvoiceLoading ? (
						<div className={styles.firstInvoiceDrawerState}>Loading…</div>
					) : firstInvoiceDoc ? (
						<PDFPreview document={firstInvoiceDoc} isOpen={isFirstInvoiceOpen} />
					) : (
						<div className={styles.firstInvoiceDrawerState}>No invoice to preview yet.</div>
					)}
				</div>
			</div>
			{isFirstInvoiceOpen && <div className={styles.firstInvoiceOverlay} onClick={() => setIsFirstInvoiceOpen(false)} />}

			{/* Modals */}
			{documentsList && (
				<PDFPreviewModal
					isOpen={isPDFViewerOpen}
					isContractTimelineOpen
					documentsList={documentsList}
					activationStartDate={documentsList[0]?.issueDate}
					activationEndDate={documentsList[documentsList.length - 1]?.issueDate}
					onClose={() => setIsPDFViewerOpen(false)}
					onDownload={onDownloadTimelineDocument}
				/>
			)}
			<GreatJobModal
				contractType={contractType}
				showModal={showGreatJobModal}
				customer={customerSelected}
				contractId={contractData?.id}
				closeModal={() => setShowGreatJobModal(false)}
			/>

			<CreateCustomer
				isOpen={isEditCustomerOpen}
				customer={customerSelected}
				isPartner={isPartner}
				closeModal={() => setIsEditCustomerOpen(false)}
				refreshData={reloadCustomerDetails}
			/>

			<ActionModal
				isOpen={showExitViewModeModal}
				title={t('openingViewMode')}
				description={t('youAreExitingViewMode')}
				mainButton={{ title: t('yes'), onClick: onExitViewMode }}
				secondaryButton={{ title: t('cancel'), type: 'outlined', color: 'neutral', onClick: () => setShowExitViewModeModal(false) }}
				imgSrc={images.exitViewMode}
				titleClassName={styles.viewModeModalTitle}
				onClose={() => setShowExitViewModeModal(false)}
			/>
		</div>
	);
};
